import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { AppProvider } from "@/context/AppContext";
import { PartnerProvider, usePartner } from "@/context/PartnerContext";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";

import HomePage from "@/pages/HomePage";
import ConfiguratorPage from "@/pages/ConfiguratorPage";
import CatalogPage from "@/pages/CatalogPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import { BundlesPage, BundleDetailPage } from "@/pages/BundlesPage";
import QuickstartPage from "@/pages/QuickstartPage";
import ComparePage from "@/pages/ComparePage";
import BlogIndexPage from "@/pages/BlogIndexPage";
import BlogPostPage from "@/pages/BlogPostPage";
import SubsidyAdvisorPage from "@/pages/SubsidyAdvisorPage";
import PartnersPage from "@/pages/PartnersPage";
import PartnerDetailPage from "@/pages/PartnerDetailPage";
import CompareTray from "@/components/CompareTray";
import AutoLangBanner from "@/components/AutoLangBanner";
import LangShell from "@/components/LangShell";
import CartPage from "@/pages/CartPage";
import { LoginPage, RegisterPage, VerifyEmailPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/AuthPages";
import AccountPage from "@/pages/AccountPage";
import AdminPage from "@/pages/AdminPage";
import PartnerPortalPage, { PartnerLoginPage } from "@/pages/PartnerPortalPage";
import PartnerProfilePage from "@/pages/PartnerProfilePage";
import PartnerLandingPage from "@/pages/PartnerLandingPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentResultPage";

// Renders the partner marketing landing page when the active partner has
// landing_enabled === true, otherwise falls back to the normal HomePage.
function PartnerOrHome() {
  const { partner } = usePartner();
  if (partner && partner.landing_enabled) return <PartnerLandingPage />;
  return <HomePage />;
}

// Relative paths (no leading "/") so this set can be mounted under both "/"
// and "/p/:partnerSlug/" without duplication.
const InnerRoutes = () => (
  <Routes>
    <Route index element={<PartnerOrHome />} />
    <Route path="configurator" element={<ConfiguratorPage />} />
    <Route path="quickstart" element={<QuickstartPage />} />
    <Route path="compare" element={<ComparePage />} />
    <Route path="blog" element={<BlogIndexPage />} />
    <Route path="blog/:slug" element={<BlogPostPage />} />
    <Route path="subsidies" element={<SubsidyAdvisorPage />} />
    <Route path="partners" element={<PartnersPage />} />
    <Route path="partners/:slug" element={<PartnerDetailPage />} />
    <Route path="catalog" element={<CatalogPage />} />
    <Route path="product/:id" element={<ProductDetailPage />} />
    <Route path="bundles" element={<BundlesPage />} />
    <Route path="bundles/:id" element={<BundleDetailPage />} />
    <Route path="cart" element={<CartPage />} />
    <Route path="payment/success" element={<PaymentSuccessPage />} />
    <Route path="payment/cancel" element={<PaymentCancelPage />} />
    <Route path="login" element={<LoginPage />} />
    <Route path="register" element={<RegisterPage />} />
    <Route path="verify-email" element={<VerifyEmailPage />} />
    <Route path="forgot-password" element={<ForgotPasswordPage />} />
    <Route path="reset-password" element={<ResetPasswordPage />} />
    <Route path="account" element={<AccountPage />} />
    <Route path="admin" element={<AdminPage />} />
    <Route path="partner-login" element={<PartnerLoginPage />} />
    <Route path="partner-portal" element={<PartnerPortalPage />} />
    <Route path="over/:slug" element={<PartnerProfilePage />} />
    <Route path="leverancier/:slug" element={<PartnerProfilePage sourceType="vendor" />} />
  </Routes>
);

function App() {
  return (
    <HelmetProvider>
      <AppProvider>
        <BrowserRouter>
          <PartnerProvider>
            <Helmet defaultTitle="RECRA Solutions, Kiosk Configurator | Zelfbedieningszuilen op maat">
              <link rel="icon" href="/favicon.ico" sizes="any" />
              <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32.png" />
              <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
            </Helmet>
            <div className="min-h-screen flex flex-col">
              <AutoLangBanner />
              <Header />
              <main className="flex-1">
                <Routes>
                  {/* Partner whitelabel shell, with explicit /:langCode prefix for each supported language (incl. nl) */}
                  {["nl", "en", "de", "fr", "es", "it", "da"].map((lng) => (
                    <Route key={`p-${lng}`} path={`/p/:partnerSlug/${lng}/*`} element={<LangShell><InnerRoutes /></LangShell>} />
                  ))}
                  <Route path="/p/:partnerSlug/*" element={<InnerRoutes />} />
                  {/* Top-level language prefix routes (one per language, to avoid catching non-language top-level paths like /blog or /catalog) */}
                  {["nl", "en", "de", "fr", "es", "it", "da"].map((lng) => (
                    <Route key={lng} path={`/${lng}/*`} element={<LangShell><InnerRoutes /></LangShell>} />
                  ))}
                  <Route path="/*" element={<InnerRoutes />} />
                </Routes>
              </main>
              <Footer />
            </div>
            <CompareTray />
            <Toaster position="top-right" richColors closeButton />
          </PartnerProvider>
        </BrowserRouter>
      </AppProvider>
    </HelmetProvider>
  );
}

export default App;
